import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, Power, PowerOff, ArrowUp, ArrowDown } from "lucide-react";

const EMPTY = { label: "", href: "", sort_order: 100, enabled: true };

export default function AdminNavCategories() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/nav-categories");
      setItems(data.items || []);
    } catch { toast.error("Failed to load categories"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.label || !form.href) return toast.error("Label and href required");
    try {
      await http.post("/admin/nav-categories", { ...form, sort_order: items.length * 10 + 10 });
      toast.success("Category added");
      setAdding(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
  };

  const save = async () => {
    try {
      await http.patch(`/admin/nav-categories/${editingId}`, editForm);
      toast.success("Saved"); setEditingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id, label) => {
    if (!window.confirm(`Remove "${label}" from the Explore dropdown?`)) return;
    try { await http.delete(`/admin/nav-categories/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const toggle = async (row) => {
    try { await http.patch(`/admin/nav-categories/${row.id}`, { enabled: !row.enabled }); load(); }
    catch { toast.error("Toggle failed"); }
  };

  const move = async (idx, dir) => {
    const next = [...items];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setItems(next);
    try {
      await http.post("/admin/nav-categories/reorder", { ids: next.map((x) => x.id) });
    } catch { toast.error("Reorder failed"); load(); }
  };

  if (loading) return <div data-testid="admin-nav-loading" className="py-12" style={{ color: "var(--wofy-text-soft)" }}>Loading Explore categories…</div>;

  return (
    <div data-testid="admin-nav-categories">
      <div className="flex flex-wrap gap-3 items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl">Explore Dropdown · {items.length}</h2>
          <p className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>
            Categories that appear in the top-right "Explore" menu on every page. Enabled items are visible to the public.
          </p>
        </div>
        <button
          data-testid="admin-nav-add-btn"
          onClick={() => setAdding(!adding)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded font-semibold text-sm"
          style={{ background: "#5C6B52", color: "white" }}
        >
          <Plus size={13} /> Add category
        </button>
      </div>

      {adding && (
        <div data-testid="admin-nav-add-form" className="border rounded-lg p-4 mb-6" style={{ borderColor: "var(--wofy-line)", background: "rgba(0,0,0,0.02)" }}>
          <div className="grid md:grid-cols-2 gap-3 mb-3">
            <input data-testid="nav-label" className="border rounded px-3 py-2 text-sm" placeholder='Label (e.g. "Daily Recipes")' value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
            <input data-testid="nav-href" className="border rounded px-3 py-2 text-sm" placeholder='Href (e.g. "/recipes" or "https://…")' value={form.href} onChange={(e) => setForm({ ...form, href: e.target.value })} />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
              Visible immediately
            </label>
          </div>
          <div className="flex gap-2">
            <button data-testid="nav-create" onClick={create} className="px-4 py-2 rounded text-sm font-semibold" style={{ background: "var(--wofy-primary)", color: "white" }}>Add</button>
            <button onClick={() => { setAdding(false); setForm(EMPTY); }} className="px-4 py-2 rounded text-sm border" style={{ borderColor: "var(--wofy-line)" }}>Cancel</button>
          </div>
        </div>
      )}

      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="text-left" style={{ borderBottom: "1px solid var(--wofy-line)" }}>
            <th className="py-2 pr-2 w-12">Order</th>
            <th className="py-2 pr-2">Label</th>
            <th className="py-2 pr-2">Href</th>
            <th className="py-2 pr-2 text-center">Visible</th>
            <th className="py-2 pr-2"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((row, idx) => {
            const editing = editingId === row.id;
            return (
              <tr key={row.id} data-testid={`admin-nav-row-${row.id}`} className="border-b align-top" style={{ borderColor: "var(--wofy-line)" }}>
                <td className="py-2 pr-2">
                  <div className="flex flex-col gap-1">
                    <button data-testid={`admin-nav-up-${row.id}`} onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 rounded border disabled:opacity-30" style={{ borderColor: "var(--wofy-line)" }}><ArrowUp size={12} /></button>
                    <button data-testid={`admin-nav-down-${row.id}`} onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="p-1 rounded border disabled:opacity-30" style={{ borderColor: "var(--wofy-line)" }}><ArrowDown size={12} /></button>
                  </div>
                </td>
                {editing ? (
                  <>
                    <td className="py-2 pr-2"><input className="border rounded px-2 py-1 text-sm w-full" value={editForm.label} onChange={(e) => setEditForm({ ...editForm, label: e.target.value })} /></td>
                    <td className="py-2 pr-2"><input className="border rounded px-2 py-1 text-sm w-full" value={editForm.href} onChange={(e) => setEditForm({ ...editForm, href: e.target.value })} /></td>
                    <td className="py-2 pr-2 text-center"><input type="checkbox" checked={editForm.enabled} onChange={(e) => setEditForm({ ...editForm, enabled: e.target.checked })} /></td>
                    <td className="py-2 pr-2">
                      <div className="flex gap-1">
                        <button data-testid={`admin-nav-save-${row.id}`} onClick={save} className="p-1.5 rounded border" style={{ borderColor: "var(--wofy-line)" }}><Save size={13} /></button>
                        <button onClick={() => setEditingId(null)} className="p-1.5 rounded border" style={{ borderColor: "var(--wofy-line)" }}><X size={13} /></button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="py-2 pr-2 font-semibold">{row.label}</td>
                    <td className="py-2 pr-2 text-xs font-mono" style={{ color: "var(--wofy-text-soft)" }}>{row.href}</td>
                    <td className="py-2 pr-2 text-center">
                      <button data-testid={`admin-nav-toggle-${row.id}`} onClick={() => toggle(row)} title={row.enabled ? "Hide" : "Show"}>
                        {row.enabled ? <Power size={16} style={{ color: "#5C6B52" }} /> : <PowerOff size={16} style={{ color: "#b23a3a" }} />}
                      </button>
                    </td>
                    <td className="py-2 pr-2">
                      <div className="flex gap-1">
                        <button data-testid={`admin-nav-edit-${row.id}`} onClick={() => { setEditingId(row.id); setEditForm(row); }} className="p-1.5 rounded border text-xs" style={{ borderColor: "var(--wofy-line)" }}>Edit</button>
                        <button data-testid={`admin-nav-delete-${row.id}`} onClick={() => remove(row.id, row.label)} className="p-1.5 rounded border" style={{ borderColor: "var(--wofy-line)", color: "#b23a3a" }}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
